import { useEffect, useState, useMemo, memo } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "@reach/router";
import normalizePhones from './normalizePhones'
import sanitize from '../../../EHR/V2/sanitize'
import { ReactComponent as IconGlobeAmericas } from 'bootstrap-icons/icons/globe-americas.svg'
import { ReactComponent as IconChatLeftTextFill } from 'bootstrap-icons/icons/chat-left-text-fill.svg'

import useMutation from "hooks/useMutation";
import useUser from "hooks/useUser";
import WaMe from "Components/WaMe/WaMe";
import useTicker from "./useTicker";
import Form from "../../Form/Form";
import Info from "../../Info";
import PaymentLinks from "./PaymentLinks";
import Facturar from "./Facturar";
import useStoreRooms from "App/Room/useStore";
import { formatDate } from "config/i18n";

const {
  REACT_APP_MEET_URL,
  REACT_APP_API_URL
} = process.env

// Fix responsive (2026-08-14): fila normal de la agenda virtualizada
// (react-window, ver Agenda.js) mide 100px de alto — alcanza porque hora y
// paciente son una sola línea cada uno en columnas de ancho fijo. En
// "compact" (viewport angosto, ver COMPACT_BREAKPOINT en Agenda.js) esas
// columnas dejan de tener ancho fijo y el nombre del paciente puede
// truncar en menos espacio horizontal — se exporta esta altura para que
// Agenda.js se la pase a react-window como itemSize y ambos coincidan
// (FixedSizeList no soporta alturas por-fila calculadas en runtime).
export const COMPACT_ROW_HEIGHT = 100

export const waMessage = (event, t) => {
  try {
    const today = new Date().toLocaleDateString("fr-ca");
    const tomorrow = new Date(
      Date.now() + 24 * 60 * 60 * 1000
    ).toLocaleDateString("fr-ca");
    let date
    if (today === event.day) {
      date = `${t('wa_reminder_helper_today', 'dia de hoy a las')} ${event.time}`
    } else if (tomorrow === event.day) {
      date = `${t('wa_reminder_helper_tomorrow', 'dia de mañana a las')} ${event.time}`
    } else  {
      date = formatDate(new Date(`${event.day}T${event.time}`), "EEEE d MMMM yyyy HH:mm")
    }
    // Bug real (auditoría 2026-08-16): sin el fallback `= lastName`, un
    // label sin coma (consumer/empresa/walk-in con solo apellido — ver
    // onChangeConsumer.js, que arma el label con .filter(Boolean).join(", "))
    // dejaba `firstName` en `undefined`, y el `.charAt` de abajo tiraba
    // dentro del try/catch de esta función: el recordatorio de WhatsApp
    // (día/hora/servicio/link) desaparecía en silencio, sin ningún error
    // visible para el profesional.
    const [lastName, firstName = lastName] = event.consumer.label.split(',').map(p => p.trim());
    const consumer = firstName.charAt(0).toUpperCase() + firstName.slice(1);
    const service = event.service.label.replace('/', '-');
    const resource = event.resource.label.replace(/,/g, '')
    const location = event.location?.address || ''
    // Hallazgo 2026-07-27: con .filter(Boolean), un event.remote sin
    // event.token (turno legacy/importado sin migrar) armaba igual un link
    // de 2 segmentos (.../events/<id>, sin token) — indistinguible de uno
    // real a simple vista, pero sin el secreto que protege el acceso a la
    // sala (confirmado: joins reales con ese patrón en logs de prod). Nunca
    // mandar ese link: si falta el token, el recordatorio cae al texto no-
    // remoto (sin mención de videollamada) en vez de un link roto/inseguro.
    const hasVideoLink = event.remote && event.token
    const link = hasVideoLink ? [REACT_APP_MEET_URL, event.id, event.token].filter(Boolean).join('/') : ''
    const payload = {
      icon1: '',
      icon2: '',
      consumer,
      date,
      resource,
      service,
      location,
      link,
      time: event.time,
      day: event.day,
    }

    const prefix = hasVideoLink ? 'remote' : ''
    const key = ['wa', prefix, 'reminder', 'mobile'].filter(Boolean).join('_')
    return t(key, payload)
  } catch {}
}

function Event({ event, compact }) {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useUser();
  const distance = useTicker(event);
  const navigate = useNavigate();
  const openRoom = useStoreRooms((state) => state.open);
  const [form, setForm] = useState(false);

  const [mutate] = useMutation(window._l(`/${event.id}`).substr(1), {
    onError: (error) => {
      window.notify({
        message: error.message,
        type: "danger",
        title: "Error",
      });
    },
  });

  const hasEhr = user.actions.has("ehr");
  let due = event.pricing?.total

  if (event.mercadopago?.payment?.status === 'approved') {
    due = event.pricing?.total - (event.mercadopago?.payment?.transaction_amount || 0)
  }

  const changeStatus = (status) => {
    if (event.status === status) return;
    // Bug real (auditoría 2026-08-16): ningún botón de este dropdown pedía
    // confirmación antes de cancelar/marcar "no asistió" un turno real —
    // a diferencia de Form.js (toggle()), que sí guarda esta misma
    // confirmación para "cancelled". Un mis-tap en el dropdown de la
    // agenda (el camino más usado de la app) cancelaba/marcaba ausente un
    // turno sin ningún undo. Se reusa la misma clave de traducción que
    // Form.js para mantener la copia consistente en toda la app.
    //
    // Bug real (Trello "V2 | Arreglos de Responsive Design", seguimiento
    // 2026-09-08, Juan Cruz QA): "noshow" (Ausente) reusaba el mismo texto
    // de "cancelled" ("¿Confirmar cancelacion?"), aunque no es lo que se
    // está confirmando. Form.js no tiene este problema porque su toggle()
    // solo pide confirmación para 'cancelled', nunca para 'noshow'.
    if (
      (status === "cancelled" || status === "noshow") &&
      !window.confirm(
        status === "noshow"
          ? t('event_noshow_confirmation', '¿Confirmar ausente?')
          : t('event_cancel_confirmation', '¿Confirmar cancelacion?')
      )
    ) return;
    const payload = JSON.clone(event);
    payload.states = payload.states || [];
    payload.states.push({ createdAt: Date.now(), status });
    payload.status = status;
    mutate(payload);
  };


  const identification = useMemo(() => {
    const id = event.consumer?.identification?.toString().replace(/\D/g, "")
    if (!id) return null
    return parseInt(id).toLocaleString("es-AR")
  }, [event.consumer?.identification])

  const phones = useMemo(() => normalizePhones({ ...event.consumer }), [event.consumer])

  const today = new Date().toLocaleDateString("fr-ca");

  const waText = useMemo(() => waMessage(event, t), [event, t])

  // Fix responsive (2026-08-14): financiador + cobro + Facturar + links de
  // pago vivían SOLO en la columna `d-none d-lg-block` de abajo — en mobile
  // (compact) esa columna se oculta del todo y no hay ningún otro lugar en
  // esta pantalla desde donde facturar o ver/copiar un link de pago (el
  // popover de edición de Form.js no incluye estos componentes). Se extrae
  // a una variable para reusarla tal cual en la columna de desktop Y dentro
  // del popover de "hora" cuando es compact, en vez de ocultar la
  // funcionalidad por completo en mobile.
  const paymentInfo = (
    <>
      {/* Bug real (auditoría 2026-08-25, reportado por Juan Cruz QA): un
          turno real (paciente particular, sin cobertura asignada) no tiene
          event.financier — accederlo sin guard crasheaba TODA la agenda al
          renderizar ese turno. No es un caso raro (bloqueo de día): es
          cualquier turno sin financiador cargado. */}
      {event.financier && (
        <div className="text-truncate small">
          {t(event.financier.key, event.financier.label, { excludeSuffix: true })}
        </div>
      )}
      {event.financier?.identification && (
        <div className="text-truncate">
          <small className="form-text">{event.financier.identification}</small>
        </div>
      )}
      <div className="small">
        {event.pricing && (
          !due ? (
            <span className="text-success">
              ${event.pricing?.total} - <span className="text-capitalize">{t('paid', 'Pagado')}</span>
            </span>
          ) : event.source === "crontu" ? (
            <span data-tooltip-id="app-tooltip" data-tooltip-content={t('event_crontu_payment', "Monto pendiente de pago por el paciente")} className="text-danger text-capitalize">{t('collect_pending_payment', 'Cobrar')} ${due}</span>
          ) : (
            <a target="_blank" rel="noopener noreferrer" data-tooltip-id="app-tooltip" data-tooltip-content={t('event_open_payment_link', "Abrir link de pago en una nueva pestaña")} href={`${REACT_APP_API_URL}/${event.id}/pay`} className="text-danger">
              <span className="text-capitalize">{t('collect_pending_payment', 'Cobrar')}</span> ${due}
            </a>
          )
        )}
        {' '}
        <Facturar event={event} phones={phones} />
      </div>
      <PaymentLinks
        reference={event.id}
        phones={phones}
        event={event}
      />
    </>
  )

  return (
    <div
      className={cx(`d-flex align-items-center gap-4 px-1 py-2`, {
        "bg-warning bg-gradient bg-opacity-25 shadow-sm": event.status === "arrived",
        "bg-primary bg-gradient bg-opacity-25 shadow-sm": event.status === "active",
        "bg-success bg-gradient bg-opacity-25 shadow-sm": event.status === "fulfilled",
        "bg-danger bg-gradient bg-opacity-25 shadow-sm": ["cancelled", "noshow"].includes(event.status),
        "bg-white border-bottom": ["booked", "pending"].includes(event.status),
      }) }
        // Bug real (auditoría 2026-08-31, reportado por Juan Cruz QA): esta
        // tarjeta restaba 280px (el ancho del Sidebar) de su propio 100% —
        // pero su contenedor real (Events.js: <div className="d-flex ...">
        // <Sidebar /><Router className="flex-fill" id="main-container">)
        // YA le da a #main-container el ancho correcto vía flexbox (el
        // Sidebar es un hermano en flujo normal a partir de lg, no un
        // overlay — ver Sidebar/styles.module.scss). Restar 280px acá era
        // una SEGUNDA resta sobre un contenedor que ya venía descontado,
        // dejando ~280px de espacio vacío a la derecha de cada turno.
        // Confirmado en vivo: con el sidebar abierto, el wrapper de
        // react-window (el padre directo) medía 1156px de ancho real, pero
        // esta tarjeta quedaba en 876px (1156-280) en vez de ocupar el 100%.
        style={{ width: "100%", height: compact ? COMPACT_ROW_HEIGHT : 100 }}>
      <div style={compact ? { flex: "0 0 auto" } : { width: "125px" }} className="py-2 text-center">
        {/* Bug real (auditoría 2026-08-21): sin flex-wrap, hasta 3 íconos +
            el botón de hora competían por una sola línea en esta columna
            "auto" — como es la única columna que NO se achica (flex: 0 0
            auto), cualquier exceso de ancho se lo restaba a la columna del
            paciente (la única con minWidth:0), pudiendo dejar el nombre
            invisible en mobile. */}
        <div className="d-flex align-items-center justify-content-center gap-1 mb-1 flex-wrap">
          <div className="mt-n1">
            <Info event={event} />
          </div>
          {(event.instructions || event.comments) && (
            <div className="mt-n1">
              <IconChatLeftTextFill
                data-tooltip-id="app-tooltip"
                // Bug real (auditoría 2026-08-16): event.comments/instructions
                // son texto libre tipeado por el equipo (Form.js, campos
                // "internal_note"/"consumer_message") interpolado crudo en un
                // string de HTML pasado a data-tooltip-html — react-tooltip
                // lo renderiza vía dangerouslySetInnerHTML internamente,
                // mismo vector de XSS ya fixeado en otros tooltips de la app.
                data-tooltip-html={sanitize([event.comments, event.instructions].filter(Boolean).join("<hr />"))}
                width="14"
                height="14"
                className="text-secondary"
              />
            </div>
          )}
          {event.source !== "drmanager" && (
            <div className="mt-n1">
              <IconGlobeAmericas
                data-tooltip-id="app-tooltip"
                data-tooltip-content={t(`source_${event.source}`)}
                width="14"
                height="14"
                className="text-secondary"
              />
            </div>
          )}
          <button
            className={cx("btn text-truncate p-0", {
              "link-danger": event.overbooking,
              "link-primary": !event.overbooking,
            })}
            // Bug real (auditoría 2026-08-21): text-truncate no tenía nada
            // contra qué recortar — sin un maxWidth, el botón crecía a su
            // ancho natural (ej. el fallback "Turno sin horario", 18
            // caracteres) e inflaba esta columna "auto" en compact.
            style={compact ? { maxWidth: 90 } : undefined}
            onClick={() => setForm(!form)}
          >
            {event.time || event.range?.split('-')?.join(' ') || t('no_time_setup', 'Turno sin horario')}
          </button>
        </div>
        {form && (
          <div
            style={{ width: "min(356px, calc(100vw - 24px))", zIndex: 1000 }}
            className={`p-3 mt-2 border shadow-lg rounded bg-white position-absolute text-start`}
          >
            <Form
              eventID={event.id}
              onClose={() => setForm(false)}
              onChange={(newEvent) => {
                setForm(false);
              }}
            />
            {/* Fix responsive (2026-08-14): en compact la columna de
                financiador/Facturar/links de pago está oculta (ver más
                abajo) — se suma acá para que siga siendo alcanzable con un
                toque, en vez de perderse por completo en mobile. */}
            {compact && (
              <div className="mt-2 pt-2 border-top">
                {paymentInfo}
              </div>
            )}
          </div>
        )}
        {distance && (
          <div className="text-secondary text-xs lh-1 text-truncate">{distance}</div>
        )}
        <div className="text-secondary text-truncate rounded mt-1 lh-1 text-xs">
          {t(`status_${event.status}`, { context: "events" })}
        </div>
      </div>
      <div style={compact ? { flex: "1 1 auto", minWidth: 0 } : { width: "320px" }} className="py-2 text-truncate">
        <Link
          className="link-dark"
          type="button"
          data-consumer={JSON.stringify({ id: event.consumer?.id })}
          data-bs-toggle="modal"
          data-bs-target="#consumersModal"
          to={window._l(`/${event.consumer?.id}`)}
        >
          {event.consumer?.label}
        </Link>
        <div className="text-truncate d-flex gap-3 align-items-center small opacity-75">
          {phones.map((phone, index) => (
            <WaMe
              key={`${index}`}
              text={waText}
              phone={phone}
            />
          ))}
          {identification && <span className="text-secondary">{identification}</span>}
        </div>
      </div>
      <div style={{ width: "170px" }} className="d-none d-lg-block py-2">
        {paymentInfo}
      </div>
      <div className="d-none d-lg-block py-2 overflow-hidden flex-fill">
        <div className="text-truncate">
          {/* Bug real (auditoría 2026-08-25, reportado por Juan Cruz QA):
              confirmado con datos reales del entorno QA que service/
              resource/location pueden faltar en un turno real (además de
              financier, ya arreglado más arriba) — accederlos sin guard
              crasheaba toda la agenda. */}
          {(t(event.service?.key, event.service?.label) || '')
            .split("/")
            .reverse()
            .map((key, index) => (
              <span
                key={key}
                className={index !== 0 ? "small text-secondary" : "me-2"}
              >
                {key}
              </span>
            ))}
        </div>
        <div>
          <small>{event.resource?.label}</small>
          {!event.remote && (
            <small className="text-secondary ms-2">{event.location?.label}</small>
          )}
        </div>
      </div>
      {/* Bug real (Trello "V2 | Arreglos de Responsive Design", 2026-09-04):
          esta columna no tenía ancho fijo, a diferencia de las otras 3
          columnas de esta misma tarjeta (hora 125px, paciente 320px,
          financiador 170px) — con "d-flex flex-column" sin width, el
          navegador la achica/agranda al ancho del hijo más ancho de CADA
          tarjeta particular, y el contenido varía según el status del turno
          (ej: "atendido" solo renderiza un botón corto; "activo"/"reservado"
          suman además "Ir a HCE"/"Iniciar"/"Videoconsulta"). Resultado:
          tarjetas con distinto status quedaban con anchos visiblemente
          distintos entre sí. Medido en un harness estático con los
          textos reales: el escenario más ancho ("Ir a HCE" + "En consulta")
          da ~151px — 160px deja margen. Los botones de adentro ya usan
          flex-fill/text-truncate, así que ocupan el 100% de este ancho fijo
          sin cambios adicionales. */}
      <div className="d-flex flex-column ms-auto" style={{ width: "160px" }}>
        {event.remote && (
          <button
            className="btn btn-sm bg-light link-primary mb-1 d-flex justify-content-between align-items-center"
            onClick={() => {
              openRoom(`${event.id}/${event.token}`);
              // Reporte QA (comments V2, Juan Cruz): este botón abría la
              // llamada y arrancaba el contador de la HCE sin pasar el turno
              // a "active" (a diferencia de "Iniciar", más abajo) — al
              // finalizar desde el contador, endConsultation() no-opeaba
              // (status !== "active") y el contador seguía corriendo para
              // siempre. Mismo changeStatus("active") que ya usa "Iniciar",
              // para que ambos caminos de entrada dejen el turno consistente.
              changeStatus("active");
              // Pedido de reunión 2026-07-29 (Juan Cruz): además de abrir el
              // modal de la llamada, navegar la página a la HCE del
              // paciente — la llamada queda flotando encima mientras se ve
              // la HCE debajo. Mismo gate de permiso que el botón "Ir a
              // HCE" (Room.js) y el propio botón de HCE de esta agenda,
              // más abajo.
              if (hasEhr) {
                navigate(
                  window._l(`/${event.consumer.id}/ehr?eventID=${event.id.split("/")[1]}`)
                );
              }
            }}
          >
            <div>{t("open_remote", "Videconsulta")}</div>
            <div>&rarr;</div>
          </button>
        )}
        {today === event.day && (
          <>
            {hasEhr && ["booked", "arrived"].includes(event.status) ? (
              <button
                className="btn btn-sm bg-light link-primary mb-1 d-flex justify-content-between align-items-center"
                onClick={() => {
                  changeStatus("active");
                  navigate(
                    window._l(
                      `/${event.consumer.id}/ehr?eventID=${
                        event.id.split("/")[1]
                      }`
                    )
                  );
                }}
              >
                <div>{t("start", { context: "agenda" })}</div>
                <div>&rarr;</div>
              </button>
            ) : (
              hasEhr &&
              event.status === "active" && (
                <Link
                  className="btn btn-sm bg-light link-primary mb-1 d-flex justify-content-between align-items-center"
                  to={window._l(
                    `/${event.consumer.id}/ehr?eventID=${
                      event.id.split("/")[1]
                    }`
                  )}
                >
                  <div>{t("ehr", { context: "agenda" })}</div>
                  <div>&rarr;</div>
                </Link>
              )
            )}
            <div className="dropdown d-flex align-items-center">
              {event.status === "booked" ? (
                <button
                  className="flex-fill btn btn-sm bg-light link-primary text-truncate text-start d-flex align-items-center justify-content-between"
                  onClick={() => changeStatus("arrived")}
                  type="button"
                >
                  {/* Bug real (auditoría 2026-08-21): text-truncate en el
                      <button> no hacía nada — el texto está en un <div>
                      anidado (una caja de bloque), y text-overflow:ellipsis
                      solo aplica al contenido inline PROPIO del elemento,
                      no a hijos en bloque. Sin min-width:0 tampoco se podía
                      achicar dentro del flex row. El resultado: en vez de
                      "…", el label se recortaba a lo bruto a mitad de
                      carácter cuando no entraba. */}
                  <div className="text-truncate" style={{ minWidth: 0 }}>
                    {t(`status_${event.status}`, { context: "events" })}
                  </div>
                  <div className="flex-shrink-0">&rarr;</div>
                </button>
              ) : event.status === "arrived" ? (
                <button
                  className="flex-fill btn btn-sm bg-light link-primary text-truncate text-start d-flex align-items-center justify-content-between"
                  onClick={() => changeStatus("active")}
                  type="button"
                >
                  {/* Bug real (auditoría 2026-08-21): mismo motivo que el
                      botón de "booked" de arriba. */}
                  <div className="text-truncate" style={{ minWidth: 0 }}>
                    {t(`status_${event.status}`, { context: "events" })}
                  </div>
                  <div className="flex-shrink-0">&rarr;</div>
                </button>
              ) : event.status === "active" ? (
                <button
                  className="flex-fill btn btn-sm bg-light link-success text-truncate text-start d-flex align-items-center justify-content-between"
                  onClick={() => changeStatus("fulfilled")}
                  type="button"
                >
                  {/* Bug real (auditoría 2026-08-21): mismo motivo que los
                      botones de arriba. */}
                  <div className="text-truncate" style={{ minWidth: 0 }}>
                    {t(`status_${event.status}`, { context: "events" })}
                  </div>
                  <div className="flex-shrink-0">&rarr;</div>
                </button>
              ) : event.status === "fulfilled" ? (
                <button
                  className="flex-fill btn btn-sm bg-light link-success d-flex text-truncate text-start d-flex align-items-center justify-content-between"
                  type="button"
                >
                  {t(`status_${event.status}`, { context: "events" })}
                </button>
              ) : (
                <button
                  className="flex-fill btn btn-sm bg-light link-success text-truncate text-start d-flex align-items-center justify-content-between"
                  type="button"
                >
                  {t(`status_${event.status}`)}
                </button>
              )}
              {/* Fix responsive (2026-08-14): dropdown-toggle-split de
                  Bootstrap es solo el caret, ~24px de ancho — hit-target
                  chico para una acción táctil de uso constante en la
                  agenda. */}
              <button
                type="button"
                id={`status-dropdown-toggle-${event.id}`}
                className="btn btn-sm bg-light link-dark dropdown-toggle dropdown-toggle-split"
                style={{ minWidth: 40 }}
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <span className="visually-hidden">
                  {t("toggle_dropdown")}
                </span>
              </button>
              {/* Bug real (auditoría 2026-08-16): aria-labelledby apuntaba a
                  un id ("dropdownMenuButton") que ningún elemento tenía —
                  referencia ARIA colgante (sin nombre accesible para
                  lectores de pantalla), y si alguna vez se le hubiera
                  asignado ese id literal al botón de arriba, cada tarjeta
                  de turno renderizada a la vez en la agenda habría
                  compartido el mismo id, rompiendo la asociación para
                  todas menos una. Se usa un id único por turno. */}
              <ul
                className="dropdown-menu"
                aria-labelledby={`status-dropdown-toggle-${event.id}`}
              >
                <li>
                  <h6 className="dropdown-header">
                    {t("change_event_status")}
                  </h6>
                </li>
                <li>
                  <button
                    className={cx("btn btn-sm dropdown-item", {
                      "fw-bold": event.status === "booked",
                    })}
                    onClick={() => changeStatus("booked")}
                  >
                    {t("status_booked", { context: "events" })}
                  </button>
                </li>
                <li>
                  <button
                    className={cx("btn btn-sm dropdown-item", {
                      "fw-bold": event.status === "arrived",
                    })}
                    onClick={() => changeStatus("arrived")}
                  >
                    {t("status_arrived", { context: "events" })}
                  </button>
                </li>
                <li>
                  <button
                    className={cx("btn btn-sm dropdown-item", {
                      "fw-bold": event.status === "active",
                    })}
                    onClick={() => changeStatus("active")}
                  >
                    {t("status_active", { context: "events" })}
                  </button>
                </li>
                <li>
                  <button
                    className={cx("btn btn-sm dropdown-item", {
                      "fw-bold": event.status === "fulfilled",
                    })}
                    onClick={() => changeStatus("fulfilled")}
                  >
                    {t("status_fulfilled", { context: "events" })}
                  </button>
                </li>
                <li>
                  <button
                    className={cx("btn btn-sm dropdown-item", {
                      "fw-bold": event.status === "cancelled",
                    })}
                    onClick={() => changeStatus("cancelled")}
                  >
                    {t("status_cancelled", { context: "events" })}
                  </button>
                </li>
                <li>
                  <button
                    className={cx("btn btn-sm dropdown-item", {
                      "fw-bold": event.status === "noshow",
                    })}
                    onClick={() => changeStatus("noshow")}
                  >
                    {t("status_noshow", { context: "events" })}
                  </button>
                </li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// export default Event;
export default memo(Event, (prev, next) =>
  prev.event.updatedAt === next.event.updatedAt &&
  JSON.stringify(prev.event.paymentLinks) === JSON.stringify(next.event.paymentLinks) &&
  // invoice: lo setea el worker de ARCA server-side al aprobar el comprobante —
  // sin esto el botón no reflejaría "Facturado" hasta recargar (plan §5.2)
  JSON.stringify(prev.event.invoice) === JSON.stringify(next.event.invoice)
)

